import type { ReactNode } from 'react';
import {
  Bookmark,
  Camera,
  Heart,
  Home,
  MessageCircle,
  MoreHorizontal,
  Plus,
  Search,
  Send,
  ShoppingBag,
  Video,
} from 'lucide-react';

import { BrandAvatar } from './BrandAvatar';
import { ExpandableCaption } from './ExpandableCaption';
import { PhoneFrame, StatusBar } from './PhoneFrame';
import { PreviewMedia } from './PreviewMedia';
import { useTranslation } from 'react-i18next';

interface InstagramPreviewProps {
  caption: string;
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
  mediaSlot?: ReactNode;
  displayName: string;
  handle: string;
  avatarUrl: string | null;
}

export const InstagramPreview = ({
  caption,
  mediaUrl,
  mediaType,
  mediaSlot,
  displayName,
  handle,
  avatarUrl,
}: InstagramPreviewProps) => {
  const { t } = useTranslation('contentTest');
  return (
    <PhoneFrame bgcolor="#fff" scrollable>
      <StatusBar tone="dark" />

      <div className="relative flex items-center justify-center gap-1.5 py-2 border-b border-black/10">
        <span className="text-base font-bold">{handle}</span>
        <span className="w-1.5 h-1.5 rounded-full bg-[#ed4956]" />
        <div className="absolute right-3 text-black">
          <MoreHorizontal size={18} />
        </div>
      </div>

      <div className="flex items-center gap-2 px-2.5 py-2">
        <BrandAvatar
          avatarUrl={avatarUrl}
          size={32}
          alt={displayName}
          // eslint-disable-next-line i18next/no-literal-string -- CSS border shorthand
          ring="2px solid transparent"
        />
        <div className="flex-1">
          <p className="text-[13px] font-bold">{handle}</p>
          <p className="text-[11px] text-black/55">
            {t('contentTest.instagramPreview.originalAudio')}
          </p>
        </div>
        <div className="text-black">
          <MoreHorizontal size={18} />
        </div>
      </div>

      <div
        data-testid="phone-preview-media-frame"
        className="relative flex shrink-0 items-center justify-center bg-[#efefef]"
      >
        {mediaSlot ? (
          // Variant cards inject their own media, sized to fill a square box.
          <div className="relative aspect-square w-full">{mediaSlot}</div>
        ) : mediaUrl ? (
          // Uploaded media shows at its natural aspect (never cropped), capped to
          // Instagram's tallest allowed portrait (4:5); taller images letterbox on
          // the frame background instead of being cut off.
          <PreviewMedia
            mediaUrl={mediaUrl}
            mediaType={mediaType}
            className="block w-full h-auto max-h-[385px] object-contain"
          />
        ) : (
          <div className="flex aspect-square w-full flex-col items-center justify-center gap-1">
            <Camera size={36} color="rgba(0,0,0,0.45)" />
            <p className="text-[13px] text-black/55">{t('contentTest.instagramPreview.noMedia')}</p>
          </div>
        )}

        <div className="absolute bottom-2 left-2">
          <div className="inline-flex items-center gap-1 bg-black/60 text-white px-2 py-1 rounded-full">
            <ShoppingBag size={14} />
            <span className="text-xs font-semibold">
              {t('contentTest.instagramPreview.viewProducts')}
            </span>
          </div>
        </div>
      </div>

      <div className="px-2.5 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Heart size={22} />
            <MessageCircle size={22} />
            <Send size={22} />
          </div>
          <Bookmark size={22} />
        </div>
        <p className="text-[13px] font-bold mt-1.5">
          {t('contentTest.instagramPreview.n12453Likes')}
        </p>
        <ExpandableCaption
          data-testid="phone-preview-caption"
          className="text-[13px] mt-0.5 text-left"
        >
          <span className="font-bold mr-1">{handle}</span>
          {caption}
        </ExpandableCaption>
      </div>

      <div className="flex items-center justify-around py-1.5 border-t border-black/10">
        <Home size={22} />
        <Search size={22} />
        <div className="flex items-center justify-center w-[22px] h-[22px] border-2 border-black rounded">
          <Plus size={14} />
        </div>
        <Video size={22} />
        <BrandAvatar avatarUrl={avatarUrl} size={22} alt={displayName} />
      </div>
    </PhoneFrame>
  );
};

InstagramPreview.displayName = 'InstagramPreview';
